/**
 * ARCOIRIS CUSTOM - Gestion du workflow de précommande
 *
 * États possibles :
 * - preview : la précommande n'est pas encore ouverte
 * - open    : la précommande est ouverte
 * - closed  : la date de fin est dépassée
 *
 * Le produit n'arrive jamais ici lorsqu'il est "normal".
 *
 * Règles :
 * - avant la date de début : achat bloqué
 * - à la date de début : rechargement automatique de la page
 *   pour laisser Venue recalculer proprement la variante et les boutons
 * - pendant la précommande : achat autorisé
 * - à la date de fin : achat bloqué immédiatement
 */

const MAX_TIMEOUT = 60 * 60 * 1000; // 1 heure
const initializedPreorderForms = new WeakSet();

const initArcoIrisPreorder = (productForm) => {
    if (initializedPreorderForms.has(productForm)) return;
  const form = productForm.querySelector('form');
  const addButton = productForm.querySelector('.js-product-add');
  const buttonsWrapper = productForm.querySelector('.js-product-buttons');
  const buttonText = productForm.querySelector('.staged-action__text');
  const dynamicButton = productForm.querySelector('dynamic-button');

  if (!form || !addButton) return;

  const startRaw = productForm.dataset.preorderStart;
  const deadlineRaw = productForm.dataset.preorderDeadline;

  const startDate = startRaw ? new Date(startRaw) : null;
  const deadlineDate = deadlineRaw ? new Date(deadlineRaw) : null;

  const previewLabel =
    productForm.dataset.preorderPreviewLabel ||
    'Précommande bientôt disponible';

  const closedLabel =
    productForm.dataset.preorderClosedLabel ||
    'Précommandes fermées';

  const openLabel =
    productForm.dataset.preorderOpenLabel ||
    'Précommander';
  /**
   * Sécurité :
   * un produit géré par ce script doit obligatoirement
   * posséder une date de début valide.
   */
  if (!startDate || Number.isNaN(startDate.getTime())) return;
    initializedPreorderForms.add(productForm);

  /**
   * Retourne l'état réel à l'instant T.
   *
   * On ne se fie pas uniquement au data-preorder-state généré
   * par Liquid, car la page peut rester ouverte plusieurs minutes.
   */
  const getState = () => {
    const now = Date.now();

    if (now < startDate.getTime()) {
      return 'preview';
    }

    if (
      deadlineDate &&
      !Number.isNaN(deadlineDate.getTime()) &&
      now >= deadlineDate.getTime()
    ) {
      return 'closed';
    }

    return 'open';
  };

  let lockedState = null;
  let timerId = null;

  /**
   * Applique uniquement les modifications nécessaires.
   *
   * Important :
   * ne pas réécrire continuellement les mêmes attributs,
   * sinon MutationObserver peut créer une boucle.
   */
  const enforceLockedState = () => {
    if (lockedState !== 'preview' && lockedState !== 'closed') return;

    const label =
      lockedState === 'preview'
        ? previewLabel
        : closedLabel;

    if (!addButton.disabled) {
      addButton.disabled = true;
    }

    if (addButton.getAttribute('aria-disabled') !== 'true') {
      addButton.setAttribute('aria-disabled', 'true');
    }

    if (
      buttonsWrapper &&
      !buttonsWrapper.classList.contains('is-disabled')
    ) {
      buttonsWrapper.classList.add('is-disabled');
    }

    if (
      buttonText &&
      buttonText.textContent.trim() !== label
    ) {
      buttonText.textContent = label;
    }

    if (dynamicButton && !dynamicButton.hidden) {
      dynamicButton.hidden = true;
    }
  };
  /**
   * Conserve le libellé de précommande lorsque Venue
   * recalcule le bouton après un changement de variante.
   *
   * Si la variante est indisponible, on laisse Venue
   * afficher son propre état "épuisé / indisponible".
   */
  const enforceOpenState = () => {
    if (lockedState !== null) return;
    if (addButton.disabled) return;

    if (
      buttonText &&
      buttonText.textContent.trim() !== openLabel
    ) {
      buttonText.textContent = openLabel;
    }
  };
  /**
   * Active le verrou Preview ou Closed.
   */
  const lock = (state) => {
    lockedState = state;
    productForm.dataset.preorderState = state;
    enforceLockedState();
  };

  /**
   * Programme le prochain changement d'état.
   *
   * Les setTimeout sont volontairement limités à 1 heure.
   * Pour une date éloignée, le script revérifie simplement
   * périodiquement la durée restante.
   */
  const schedule = () => {
    if (timerId) {
      clearTimeout(timerId);
    }

    const state = getState();

    /**
     * PREVIEW
     *
     * On garde le produit verrouillé.
     * À l'ouverture, on recharge une fois la page afin que
     * Liquid + Venue reconstruisent proprement le formulaire.
     */
    if (state === 'preview') {
      lock('preview');

      const remaining = startDate.getTime() - Date.now();

      timerId = window.setTimeout(() => {
        if (Date.now() >= startDate.getTime()) {
          window.location.reload();
        } else {
          schedule();
        }
      }, Math.min(Math.max(remaining, 0), MAX_TIMEOUT));

      return;
    }

    /**
     * CLOSED
     */
    if (state === 'closed') {
      lock('closed');
      return;
    }

    /**
     * OPEN
     *
     * Pas de verrou.
     * S'il n'y a pas de date de fin, rien d'autre à programmer.
     */
    lockedState = null;
    productForm.dataset.preorderState = 'open';
    enforceOpenState();

    if (
      !deadlineDate ||
      Number.isNaN(deadlineDate.getTime())
    ) {
      return;
    }

    /**
     * Une date de fin existe :
     * on programme la fermeture.
     */
    const remaining = deadlineDate.getTime() - Date.now();

    timerId = window.setTimeout(() => {
      if (Date.now() >= deadlineDate.getTime()) {
        lock('closed');
      } else {
        schedule();
      }
    }, Math.min(Math.max(remaining, 0), MAX_TIMEOUT));
  };

  /**
   * Dernière barrière côté interface.
   *
   * Même si Venue ou un autre script tente de soumettre
   * le formulaire pendant Preview ou après fermeture,
   * on revérifie les dates au moment exact du submit.
   */
  form.addEventListener(
    'submit',
    (event) => {
      const state = getState();

      if (state === 'preview' || state === 'closed') {
        event.preventDefault();
        event.stopImmediatePropagation();

        lock(state);
      }
    },
    true
  );

  /**
   * Venue peut modifier le bouton lors d'un changement de variante.
   *
   * Si le produit est verrouillé, on réapplique notre état.
   */
  const observer = new MutationObserver(() => {
  const state = getState();

  if (state === 'preview' || state === 'closed') {
    lock(state);
    return;
  }

  lockedState = null;
  productForm.dataset.preorderState = 'open';
  enforceOpenState();
});

  observer.observe(addButton, {
    attributes: true,
    attributeFilter: ['disabled'],
    childList: true,
    subtree: true
  });

  schedule();
};


/**
 * Initialise les formulaires de précommande contenus
 * dans un élément donné.
 *
 * Cela couvre :
 * - les formulaires présents au chargement initial
 * - les Quick Shops injectés dynamiquement par Venue
 */
const initPreorderFormsWithin = (root) => {
  if (root instanceof Element) {
    if (root.matches('product-form[data-arcoiris-preorder]')) {
      initArcoIrisPreorder(root);
    }

    const parentForm = root.closest(
      'product-form[data-arcoiris-preorder]'
    );

    if (parentForm) {
      initArcoIrisPreorder(parentForm);
    }
  }

  root
    .querySelectorAll?.('product-form[data-arcoiris-preorder]')
    .forEach(initArcoIrisPreorder);
};


/**
 * Formulaires déjà présents au chargement.
 */
initPreorderFormsWithin(document);


/**
 * Venue charge notamment les Quick Shops dynamiquement.
 *
 * On surveille uniquement les nouveaux éléments ajoutés au DOM
 * afin d'initialiser les éventuels formulaires de précommande.
 */
const preorderDomObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;

      initPreorderFormsWithin(node);
    });
  });
});

preorderDomObserver.observe(document.documentElement, {
  childList: true,
  subtree: true
});